import type { Condition } from '@atbash-labs/ontology-agent-wire'
import { triggersFromProxyWire, triggersToProxyWire } from '@atbash-labs/ontology-agent-wire'

import type {
  CreateNotificationRulePayload,
  NotificationRule,
  NotificationTrigger,
  UpdateNotificationRulePayload
} from './types'

/**
 * Walk one trigger's condition tree and collect every leaf's `tag_path`.
 * The proxy only echoes `tag_path` on leaves it could resolve (best-effort —
 * see `Handler.resolveForRead` in `notificationrules/handlers.go`), so an
 * unresolvable/deleted tag silently drops out rather than surfacing as a
 * blank chip.
 */
function collectTagPaths(condition: Condition, into: Set<string>): void {
  switch (condition.kind) {
    case 'live':
    case 'agg':
    case 'deviation':
    case 'alarm_active':
      if (condition.tag_path) into.add(condition.tag_path)
      return
    case 'expr':
      for (const src of Object.values(condition.sources)) {
        if (src.tag_path) into.add(src.tag_path)
      }
      return
    case 'and':
    case 'or':
      for (const operand of condition.operands) collectTagPaths(operand, into)
      return
    case 'not':
      collectTagPaths(condition.operand, into)
  }
}

/**
 * Derive the sorted, deduped set of registry tag paths a rule's triggers
 * reference, for equipment-scope display (list cards + detail header). The
 * proxy `Rule` has no top-level `tag_paths` field (unlike `RCAConfig`), so
 * this is purely a client-side projection over the (best-effort resolved)
 * leaf `tag_path` echoes.
 */
export function deriveNotificationRuleTagPaths(triggers: NotificationTrigger[]): string[] {
  const set = new Set<string>()
  for (const trigger of triggers) collectTagPaths(trigger.condition, set)
  return Array.from(set).sort()
}

export function normalizeNotificationRule(rule: NotificationRule): NotificationRule {
  const triggers = triggersFromProxyWire(rule.triggers)
  return {
    ...rule,
    email_addresses: rule.email_addresses ?? [],
    triggers,
    tag_paths: deriveNotificationRuleTagPaths(triggers)
  }
}

export function normalizeNotificationRuleList(rules: NotificationRule[]): NotificationRule[] {
  return rules.map(normalizeNotificationRule)
}

export function denormalizeCreateNotificationRulePayload(
  body: CreateNotificationRulePayload
): CreateNotificationRulePayload {
  return {
    ...body,
    triggers: triggersToProxyWire(body.triggers)
  }
}

export function denormalizeUpdateNotificationRulePayload(
  patch: UpdateNotificationRulePayload
): UpdateNotificationRulePayload {
  if (!patch.triggers) return patch
  return {
    ...patch,
    triggers: triggersToProxyWire(patch.triggers)
  }
}
